/**
 * Feature Navigation Configuration
 * Defines the Category → Type hierarchy for all features
 *
 * Structure:
 * - Category: Top-level grouping (e.g., "Monitoring", "Punishment")
 * - Type: Clickable menu item that links directly to feature content (e.g., "Screen", "Input Tracking")
 */
import { roleAllowsFeature } from '../../core/roles.esm.js';

export const FEATURE_NAVIGATION = {
  ai: {
    id: 'ai',
    icon: 'iconsminds-brain',
    label: 'AI',
    description: 'Live cockpit: guide, monitor, and interject with the AI agent',
    order: 0,
    // Render as a single clickable top-level item (no expand group).
    directNav: true,
    types: {
      cockpit: {
        id: 'ai-cockpit',
        label: 'AI Cockpit',
        description: 'Live cockpit: guide, monitor, and interject with the always-on AI agent',
        icon: 'iconsminds-brain',
        to: '/app/ai/cockpit',
        featureId: 'chat',
        // No capability gate — always visible when connected.
        requiredCapability: null,
        order: 1
      }
    }
  },
  monitoring: {
    id: 'monitoring',
    icon: 'iconsminds-monitor-analytics',
    label: 'Monitoring',
    description: 'Surveillance, tracking, and behavioral analysis',
    order: 1,
    types: {
      screen: {
        id: 'screen',
        label: 'Live Screen',
        description: 'Live view of the desktop screen',
        icon: 'simple-icon-screen-desktop',
        to: '/app/monitoring/screen',
        featureId: 'screen-capture',
        requiredCapability: null,
        order: 0
      },
      timeline: {
        id: 'sacred-timeline',
        label: 'Timeline',
        description: 'Activity sessions, flags, and scheduled events',
        icon: 'simple-icon-clock',
        to: '/app/monitoring/sacred-timeline',
        featureId: 'sacred-timeline',
        requiredCapability: 'timeline',
        order: 1
      },
      fileBrowser: {
        id: 'file-browser',
        label: 'File Browser',
        description: 'Browse files on the remote desktop',
        icon: 'simple-icon-folder-alt',
        to: '/app/monitoring/file-browser',
        featureId: 'file-browser',
        requiredCapability: 'file-browser',
        order: 2
      },
      dossier: {
        id: 'dossier',
        label: 'Behavioral Profile',
        description: 'AI-generated behavioral analysis document',
        icon: 'simple-icon-docs',
        to: '/app/monitoring/dossier',
        featureId: 'dossier',
        requiredCapability: 'dossier',
        order: 5
      },
      timeLimit: {
        id: 'time-limit',
        label: 'Limitations',
        description: 'Time, application, and website limits & blocks',
        icon: 'simple-icon-hourglass',
        to: '/app/monitoring/time-limit',
        featureId: 'time-limit',
        requiredCapability: 'time-limit',
        order: 6
      },
      exposure: {
        id: 'exposure',
        label: 'Raw Capture',
        description: 'Raw surveillance data: clipboard, deleted text, searches',
        icon: 'simple-icon-note',
        to: '/app/monitoring/exposure',
        featureId: 'sacred-timeline',
        requiredCapability: null,
        order: 7
      },
      media: {
        id: 'media-gallery',
        label: 'Media',
        description: 'Images and videos viewed on the desktop',
        icon: 'simple-icon-picture',
        to: '/app/monitoring/media',
        featureId: 'sacred-timeline',
        requiredCapability: null,
        order: 8
      },
      domainHeatmap: {
        id: 'domain-heatmap',
        label: 'Domain Heatmap',
        description: 'Visual breakdown of browsing by domain',
        icon: 'simple-icon-graph',
        to: '/app/monitoring/domain-heatmap',
        featureId: 'sacred-timeline',
        requiredCapability: null,
        order: 9
      },
      keywordDashboard: {
        id: 'keyword-dashboard',
        label: 'Keyword Flags',
        description: 'Dashboard for keyword flagging with severity badges',
        icon: 'simple-icon-flag',
        to: '/app/monitoring/keyword-dashboard',
        featureId: 'sacred-timeline',
        requiredCapability: null,
        order: 10
      },
      tabSurveillance: {
        id: 'tab-surveillance',
        label: 'Tab Surveillance',
        description: 'Live DOM snapshots of open browser tabs',
        icon: 'simple-icon-layers',
        to: '/app/monitoring/tab-surveillance',
        featureId: 'sacred-timeline',
        requiredCapability: null,
        order: 11
      },
      // discord: {
      //   id: 'discord-monitor',
      //   label: 'Discord',
      //   description: 'Monitor Discord messages and activity',
      //   icon: 'simple-icon-bubble',
      //   to: '/app/monitoring/discord',
      //   featureId: 'discord-monitor',
      //   requiredCapability: 'discord-monitor',
      //   order: 12
      // }
    }
  },
  communication: {
    id: 'communication',
    icon: 'simple-icon-bubble',
    label: 'Communication',
    description: 'Chat, voice, interrogation, and prompts',
    order: 2,
    types: {
      chat: {
        id: 'chat',
        label: 'Chat',
        description: 'AI-powered conversation with the subject',
        icon: 'simple-icon-bubble',
        to: '/app/communication/chat',
        featureId: 'chat',
        requiredCapability: 'chat',
        order: 1
      },
      interrogation: {
        id: 'interrogation',
        label: 'Interrogation',
        description: 'Structured Q&A sessions with scoring',
        icon: 'simple-icon-question',
        to: '/app/communication/interrogation',
        featureId: 'interrogation',
        requiredCapability: 'interrogation',
        order: 2
      },
      voice: {
        id: 'ai-voice',
        label: 'Voice',
        description: 'Real-time AI voice conversation',
        icon: 'simple-icon-microphone',
        to: '/app/communication/ai-voice',
        featureId: 'ai-voice',
        requiredCapability: 'ai-voice',
        order: 3
      },
      quickConfession: {
        id: 'quick-confession',
        label: 'Quick Confession',
        description: 'Pop-up questions on the desktop',
        icon: 'simple-icon-speech',
        to: '/app/communication/quick-confession',
        featureId: 'quick-confession',
        requiredCapability: 'quick-confession',
        order: 4
      },
      journal: {
        id: 'journal',
        label: 'Journal',
        description: 'Assign writing prompts and review entries',
        icon: 'simple-icon-notebook',
        to: '/app/communication/journal',
        featureId: 'journal',
        requiredCapability: 'journal',
        order: 5
      },
      userPanel: {
        id: 'user-panel',
        label: 'User Panel',
        description: 'Control the desktop overlay panel',
        icon: 'iconsminds-dashboard',
        to: '/app/communication/user-panel',
        featureId: 'user-panel',
        requiredCapability: 'economy',
        order: 6
      },
      forcedBrowser: {
        id: 'forced-browser',
        label: 'Forced Browser',
        description: 'Open a fullscreen embedded browser on the desktop',
        icon: 'simple-icon-globe',
        to: '/app/communication/forced-browser',
        featureId: 'forced-browser',
        requiredCapability: 'forced-browser',
        order: 7
      }
    }
  },
  punishment: {
    id: 'punishment',
    icon: 'iconsminds-flash-2',
    label: 'Punishment',
    description: 'Disruptions, penalties, and enforcement tools',
    order: 3,
    types: {
      spinWheel: {
        id: 'spin-wheel',
        label: 'Spin Wheel',
        description: 'Random punishment roulette',
        icon: 'iconsminds-refresh',
        to: '/app/punishment/spin-wheel',
        featureId: 'spin-wheel',
        requiredCapability: 'spin-wheel',
        order: 1
      },
      economy: {
        id: 'economy',
        label: 'Economy',
        description: 'Gem currency for rewards and penalties',
        icon: 'simple-icon-credit-card',
        to: '/app/punishment/economy',
        featureId: 'economy',
        requiredCapability: 'economy',
        order: 2
      },
      writeLines: {
        id: 'write-lines',
        label: 'Write Lines',
        description: 'Force repetitive line-writing as punishment',
        icon: 'simple-icon-pencil',
        to: '/app/punishment/write-lines',
        featureId: 'write-lines',
        requiredCapability: 'write-lines',
        order: 3
      },
      inputSabotage: {
        id: 'input-sabotage',
        label: 'Input Sabotage',
        description: 'Disrupt keyboard and mouse controls',
        icon: 'simple-icon-cursor',
        to: '/app/punishment/input-sabotage',
        featureId: 'input-sabotage',
        requiredCapability: 'input-sabotage',
        order: 4
      },
      soundBlaster: {
        id: 'sound-blaster',
        label: 'Sound Blaster',
        description: 'Play sounds on the remote desktop',
        icon: 'simple-icon-volume-2',
        to: '/app/punishment/sound-blaster',
        featureId: 'sound-blaster',
        requiredCapability: 'sound-blaster',
        order: 6
      },
      windowRearrange: {
        id: 'window-rearrange',
        label: 'Window Rearrange',
        description: 'Shuffle or rearrange desktop windows',
        icon: 'simple-icon-grid',
        to: '/app/punishment/window-rearrange',
        featureId: 'window-rearrange',
        requiredCapability: 'window-rearrange',
        order: 7,
        hidden: true
      },
      countdownTimer: {
        id: 'countdown-timer',
        label: 'Countdown Timer',
        description: 'Display a visible countdown on the desktop',
        icon: 'iconsminds-timer',
        to: '/app/punishment/countdown-timer',
        featureId: 'countdown-timer',
        requiredCapability: 'countdown-timer',
        order: 8
      },
      phantomCursor: {
        id: 'phantom-cursor',
        label: 'Phantom Cursor',
        description: 'Spawn fake cursors that follow the real one',
        icon: 'simple-icon-cursor-move',
        to: '/app/punishment/phantom-cursor',
        featureId: 'phantom-cursor',
        requiredCapability: 'phantom-cursor',
        order: 9,
        hidden: true
      },
      wallpaperHijack: {
        id: 'wallpaper-hijack',
        label: 'Wallpaper Hijack',
        description: 'Replace the desktop wallpaper remotely',
        icon: 'simple-icon-frame',
        to: '/app/punishment/wallpaper-hijack',
        featureId: 'wallpaper-hijack',
        requiredCapability: 'wallpaper-hijack',
        order: 11
      },
      quiz: {
        id: 'quiz',
        label: 'Quiz',
        description: 'Pop-up quizzes that block the screen',
        icon: 'simple-icon-list',
        to: '/app/punishment/quiz',
        featureId: 'quiz',
        requiredCapability: 'quiz',
        order: 12
      },
      eyeLock: {
        id: 'eye-lock',
        label: 'Eye Lock',
        description: 'Stare challenge using Tobii eye tracking',
        icon: 'simple-icon-eye',
        to: '/app/punishment/eye-lock',
        featureId: 'eye-lock',
        requiredCapability: 'eye-lock',
        order: 13
      },
      screenTint: {
        id: 'screen-tint',
        label: 'Screen Tint',
        description: 'Apply a color overlay to the desktop screen',
        icon: 'simple-icon-drop',
        to: '/app/punishment/screen-tint',
        featureId: 'screen-tint',
        requiredCapability: 'screen-tint',
        order: 14
      },
      auctionHouse: {
        id: 'auction-house',
        label: 'Auction House',
        description: 'Sell privileges for earned currency',
        icon: 'simple-icon-tag',
        to: '/app/punishment/auction-house',
        featureId: 'economy',
        requiredCapability: 'economy',
        order: 15
      },
    }
  },
  integrations: {
    id: 'integrations',
    icon: 'iconsminds-plug-in',
    label: 'Integrations',
    description: 'External hardware and AI provider setup',
    order: 4,
    types: {
      anthropic: {
        id: 'anthropic',
        label: 'AI Providers',
        description: 'API keys and model assignments',
        icon: 'simple-icon-key',
        to: '/app/integrations/anthropic',
        featureId: 'ai-config',
        requiredCapability: null,
        order: 0
      },
      pishock: {
        id: 'pishock',
        label: 'PiShock',
        description: 'Shock device integration and compliance testing',
        icon: 'iconsminds-flash-2',
        to: '/app/integrations/pishock',
        featureId: 'pishock',
        requiredCapability: 'pishock',
        order: 1
      },
      gazeTracking: {
        id: 'gaze-tracking',
        label: 'Tobii Eye Tracker',
        description: 'Track where they look on screen',
        icon: 'simple-icon-target',
        to: '/app/integrations/gaze-tracking',
        featureId: 'gaze-tracking',
        requiredCapability: 'gaze-tracking',
        order: 2
      },
      cameras: {
        id: 'camera-monitor',
        label: 'Cameras',
        description: 'Live webcam and security-camera feeds',
        icon: 'simple-icon-camrecorder',
        to: '/app/integrations/cameras',
        featureId: 'camera-monitor',
        requiredCapability: 'camera-monitor',
        order: 3
      }
    }
  },
  games: {
    id: 'games',
    icon: 'iconsminds-gamepad-2',
    label: 'Games',
    description: 'Interactive control games and task systems',
    order: 5,
    types: {
      chess: {
        id: 'chess',
        label: 'Chess',
        description: 'A fair game of chess with engine analysis',
        icon: 'simple-icon-game-controller',
        to: '/app/games/chess',
        featureId: null,
        requiredCapability: null,
        group: 'Strategy',
        order: 1
      },
      battleship: {
        id: 'battleship',
        label: 'Battleship',
        description: 'Naval strategy with full surveillance advantage',
        icon: 'simple-icon-target',
        to: '/app/games/battleship',
        featureId: null,
        requiredCapability: null,
        group: 'Strategy',
        order: 2
      },
      siege: {
        id: 'siege',
        label: 'Siege',
        icon: 'simple-icon-target',
        description: 'Escalating lockdown game',
        to: '/app/games/siege',
        featureId: 'siege',
        requiredCapability: 'siege',
        group: 'Strategy',
        order: 3
      },
      blackjack: {
        id: 'blackjack',
        label: 'Blackjack',
        description: 'A totally honest game of 21 against the house',
        icon: 'simple-icon-layers',
        to: '/app/games/blackjack',
        featureId: null,
        requiredCapability: null,
        group: 'Card Games',
        order: 4
      },
      goFish: {
        id: 'go-fish',
        label: 'Go Fish',
        description: 'A completely honest card game of asking and collecting',
        icon: 'simple-icon-layers',
        to: '/app/games/go-fish',
        featureId: null,
        requiredCapability: null,
        group: 'Card Games',
        order: 5
      },
      cardWar: {
        id: 'card-war',
        label: 'War',
        description: 'A completely fair card game against the desktop',
        icon: 'simple-icon-layers',
        to: '/app/games/card-war',
        featureId: null,
        requiredCapability: null,
        group: 'Card Games',
        order: 6
      },
      challenges: {
        id: 'challenges',
        label: 'Challenges',
        description: 'Assign timed tasks with rewards or penalties',
        icon: 'simple-icon-trophy',
        to: '/app/games/challenges',
        featureId: 'challenges',
        requiredCapability: 'challenges',
        group: 'Puzzles',
        order: 7
      },
      memoryMatch: {
        id: 'memory-match',
        label: 'Memory',
        description: 'Card matching game with perfect recall',
        icon: 'simple-icon-grid',
        to: '/app/games/memory-match',
        featureId: null,
        requiredCapability: null,
        group: 'Puzzles',
        order: 8
      },
      hangman: {
        id: 'hangman',
        label: 'Hangman',
        description: 'Evil Hangman where the word changes to avoid correct guesses',
        icon: 'simple-icon-pencil',
        to: '/app/games/hangman',
        featureId: null,
        requiredCapability: null,
        group: 'Puzzles',
        order: 9
      },
      jigsaw: {
        id: 'jigsaw',
        label: 'Jigsaw',
        description: 'Lock the desktop behind an image jigsaw they must reassemble',
        icon: 'simple-icon-puzzle',
        to: '/app/games/jigsaw',
        featureId: null,
        requiredCapability: null,
        group: 'Puzzles',
        order: 10
      },
      wordSearch: {
        id: 'word-search',
        label: 'Word Search',
        description: 'Lock the screen with a word search puzzle',
        icon: 'simple-icon-magnifier',
        to: '/app/games/word-search',
        featureId: 'word-search',
        requiredCapability: 'word-search',
        group: 'Puzzles',
        order: 10
      },
      dungeonCrawler: {
        id: 'dungeon-crawler',
        label: 'Dungeon',
        description: 'Roguelike dungeon with omniscient DM control',
        icon: 'simple-icon-compass',
        to: '/app/games/dungeon-crawler',
        featureId: null,
        requiredCapability: null,
        group: 'Adventures',
        order: 11
      },
      labyrinth: {
        id: 'labyrinth',
        label: 'The Labyrinth',
        description: 'Maze pursuit with monsters, traps, and stakes',
        icon: 'simple-icon-map',
        to: '/app/games/labyrinth',
        featureId: 'labyrinth',
        requiredCapability: 'labyrinth',
        group: 'Adventures',
        order: 12
      },
      // corruption: {
      //   id: 'corruption',
      //   label: 'Corruption',
      //   description: 'Progressively corrupt the desktop environment',
      //   icon: 'simple-icon-ghost',
      //   to: '/app/games/corruption',
      //   featureId: 'corruption',
      //   requiredCapability: 'corruption',
      //   order: 1
      // },
      taskBoard: {
        id: 'task-board',
        label: 'Task Board',
        description: 'Assign tasks with reward/penalty stakes',
        icon: 'simple-icon-list',
        to: '/app/games/task-board',
        featureId: 'task-board',
        requiredCapability: 'task-board',
        group: 'Tasks',
        order: 13
      },
    }
  },
  system: {
    id: 'system',
    icon: 'iconsminds-wrench',
    label: 'System',
    description: 'Settings, diagnostics, and connection management',
    order: 6,
    types: {
      configuration: {
        id: 'configuration',
        label: 'Configuration',
        description: 'Connection settings and pairing',
        icon: 'simple-icon-settings',
        to: '/app/system/configuration',
        featureId: 'config-manager',
        requiredCapability: 'config-manager',
        order: 1
      },
      // Debug moved to sidebar utility dock
    }
  }
};

/**
 * Five-verb navigation (UX overhaul, direction D).
 *
 * The sidebar is organized around what the operator wants to DO, not around
 * how the system is built. Seven engineer-facing menus (Monitoring, Punishment,
 * Communication, Games, Integrations, System, AI) collapse into five plain
 * verbs. The underlying `FEATURE_NAVIGATION` catalog and every route path are
 * unchanged — this only regroups them, so existing links, deep-links, and the
 * per-feature pages keep working exactly as before.
 *
 *   Surveil     — live: what they're doing right now (passive intake)
 *   Interrogate — make them answer: live dialogue + prompts you assign & review
 *   Investigate — history and analysis
 *   Inflict     — take action on them
 *   Configure   — connections and settings
 *
 * Surveil vs Interrogate is the passive/active line: surveillance is watching a
 * subject who does nothing on your behalf; interrogation compels a response.
 * The async elicitation tools (Journal, scheduled Interrogation) live here, not
 * under Surveil, because assigning a prompt is an action — the *responses* they
 * produce flow to the Sacred Timeline and surface under Investigate.
 */
export const NAV_VERBS = {
  watch:    { id: 'watch',    label: 'Surveil',     icon: 'simple-icon-eye',      description: 'Live — what they’re doing right now',          order: 0 },
  question: { id: 'question', label: 'Interrogate', icon: 'simple-icon-bubbles',  description: 'Make them answer — live or on your schedule',  order: 1 },
  back:     { id: 'back',     label: 'Investigate', icon: 'simple-icon-clock',    description: 'History and analysis',                         order: 2 },
  do:       { id: 'do',       label: 'Inflict',     icon: 'iconsminds-flash-2',   description: 'Take action',                                  order: 3 },
  setup:    { id: 'setup',    label: 'Configure',   icon: 'simple-icon-settings', description: 'Connections and settings',                     order: 4 },
};

// Which verb each feature (by type id) belongs to. Types not listed here are
// omitted from the sidebar (but their route still resolves).
const TYPE_VERB = {
  // Watch — live feeds
  'screen': 'watch', 'tab-surveillance': 'watch', 'camera-monitor': 'watch',
  // Look back — history & analysis
  'sacred-timeline': 'back', 'file-browser': 'back', 'dossier': 'back', 'exposure': 'back',
  'media-gallery': 'back', 'domain-heatmap': 'back', 'keyword-dashboard': 'back',
  // Do — enforcement
  'time-limit': 'do', 'spin-wheel': 'do', 'write-lines': 'do', 'input-sabotage': 'do',
  'sound-blaster': 'do', 'window-rearrange': 'do', 'countdown-timer': 'do', 'phantom-cursor': 'do',
  'wallpaper-hijack': 'do', 'quiz': 'do', 'eye-lock': 'do', 'screen-tint': 'do',
  // Interrogate — compel a response: live dialogue + assign-and-review prompts
  'chat': 'question', 'ai-voice': 'question',
  'interrogation': 'question', 'journal': 'question', 'quick-confession': 'question',
  // Do — desktop control surfaces (not communication: you drive their machine)
  'user-panel': 'do', 'forced-browser': 'do',
  // Do — rewards
  'economy': 'do', 'auction-house': 'do',
  // Games are deliberately absent here: they collapse into a single "Arcade"
  // hub row (see generateMenuData) instead of 14 individual sidebar entries.
  // Their routes and the Arcade page at /app/games are unchanged.
  // Set up — connections & config (gaze-tracking is device setup/diagnostics,
  // not a live feed: the live gaze view is a toggle on the Screen's overlay).
  'configuration': 'setup', 'ai-cockpit': 'setup', 'anthropic': 'setup', 'pishock': 'setup',
  'gaze-tracking': 'setup',
};

// Sub-divider each feature sits under, within its verb. Used for the small
// grouping labels in the sidebar (see CompactSidebar `showDivider`).
const TYPE_NAVGROUP = {
  'screen': 'Live', 'tab-surveillance': 'Live', 'camera-monitor': 'Live',
  'time-limit': 'Enforce', 'spin-wheel': 'Enforce', 'write-lines': 'Enforce', 'input-sabotage': 'Enforce',
  'sound-blaster': 'Enforce', 'window-rearrange': 'Enforce', 'countdown-timer': 'Enforce',
  'phantom-cursor': 'Enforce', 'wallpaper-hijack': 'Enforce', 'quiz': 'Enforce', 'eye-lock': 'Enforce',
  'screen-tint': 'Enforce',
  'chat': 'Live', 'ai-voice': 'Live',
  'interrogation': 'Prompt & Collect', 'journal': 'Prompt & Collect', 'quick-confession': 'Prompt & Collect',
  'user-panel': 'Enforce', 'forced-browser': 'Enforce',
  'economy': 'Rewards', 'auction-house': 'Rewards',
  // Games are surfaced via the single synthetic 'games-hub' item (group 'Games').
  'configuration': 'Connection', 'ai-cockpit': 'AI', 'anthropic': 'AI', 'pishock': 'Devices',
  'gaze-tracking': 'Devices',
};

// Deterministic order of sub-dividers so "Do" reads Enforce → Talk → Rewards →
// Games rather than alphabetically. Ungrouped items sort before any group.
const NAVGROUP_ORDER = ['Live', 'Prompt & Collect', 'Enforce', 'Rewards', 'Games', 'Connection', 'AI', 'Devices'];
const navGroupRank = (g) => (g ? NAVGROUP_ORDER.indexOf(g) : -1);

/**
 * Generate menu data structure from the feature catalog, grouped into the four
 * verbs. Filters types by available remote capabilities (optional).
 * @param {Object} remoteCapabilities - Available backend capabilities
 * @param {string|null} roleId - Capability tier of the selected device's pairing.
 *   When set (and not 'full'), features the role can't command are hidden. This
 *   is cosmetic — the desktop enforces the same role authoritatively.
 * @returns {Array} Menu data structure compatible with menu.js / CompactSidebar
 */
export function generateMenuData(remoteCapabilities = null, roleId = null) {
  const buckets = {}; // verbId -> subs[]

  Object.values(FEATURE_NAVIGATION).forEach(category => {
    Object.values(category.types).forEach(type => {
      if (type.hidden) return;

      const verbId = TYPE_VERB[type.id];
      if (!verbId) return; // not surfaced in the sidebar

      // Filter by capability if remote capabilities provided
      if (remoteCapabilities && type.requiredCapability && !remoteCapabilities[type.requiredCapability]) {
        return; // backend doesn't support it
      }

      // Hide features the selected pairing's role can't command.
      if (roleId && !roleAllowsFeature(roleId, type.featureId)) {
        return;
      }

      const cap = type.requiredCapability && remoteCapabilities
        ? remoteCapabilities[type.requiredCapability]
        : null;
      const isDisabled = cap ? cap.enabled === false : false;
      const isMuted = cap ? !!cap.muted : false;

      (buckets[verbId] || (buckets[verbId] = [])).push({
        id: type.id,
        icon: type.icon,
        label: type.label,
        description: type.description || '',
        to: type.to,
        featureId: type.featureId,
        group: TYPE_NAVGROUP[type.id] || null,
        _order: typeof type.order === 'number' ? type.order : 999,
        disabled: isDisabled,
        muted: isMuted,
      });
    });
  });

  // Games collapse into a single "Arcade" hub row rather than 14 sidebar
  // entries. Each game keeps its own route and lives on the Arcade page at
  // /app/games; here we surface just the doorway to it, shown whenever at least
  // one game is available (honouring the same capability gating as everything
  // else).
  const anyGameAvailable = Object.values(FEATURE_NAVIGATION.games.types).some(t => {
    if (t.hidden) return false;
    if (remoteCapabilities && t.requiredCapability && !remoteCapabilities[t.requiredCapability]) return false;
    if (roleId && !roleAllowsFeature(roleId, t.featureId)) return false;
    return true;
  });
  if (anyGameAvailable) {
    (buckets.do || (buckets.do = [])).push({
      id: 'games-hub',
      icon: 'iconsminds-gamepad-2',
      label: 'Arcade',
      description: 'Every game in one place',
      to: '/app/games',
      featureId: null,
      group: 'Games',
      _order: 500,
      disabled: false,
      muted: false,
    });
  }

  const menuData = [];
  Object.values(NAV_VERBS)
    .sort((a, b) => a.order - b.order)
    .forEach(verb => {
      const subs = buckets[verb.id];
      if (!subs || subs.length === 0) return;
      // Keep sub-dividers together and in intended order, then by feature order.
      subs.sort((a, b) => (navGroupRank(a.group) - navGroupRank(b.group)) || (a._order - b._order));
      subs.forEach(s => { delete s._order; });
      menuData.push({
        id: verb.id,
        icon: verb.icon,
        label: verb.label,
        description: verb.description,
        to: '#',
        subs,
      });
    });

  return menuData;
}

/**
 * Get all feature routes for React Router
 * @returns {Array} Array of route configurations
 */
export function getFeatureRoutes() {
  const routes = [];

  Object.values(FEATURE_NAVIGATION).forEach(category => {
    Object.values(category.types).forEach(type => {
      routes.push({
        path: type.to,
        featureId: type.featureId,
        label: type.label,
        category: category.id,
        type: type.id
      });
    });
  });

  return routes;
}

// Landing page for each verb — the destination when the verb itself is clicked.
// "Surveil" has no dedicated landing; the person-centric Home IS its landing.
// Single source of truth: the sidebar, breadcrumbs, and the page-title lookup
// all read this, so the verb → URL mapping can never drift between them.
export const VERB_LANDING = {
  watch:    '/app/home',
  question: '/app/interrogate',
  back:     '/app/look-back',
  do:       '/app/do',
  setup:    '/app/set-up',
};

// Normalize a pathname for matching: strip a single trailing slash (but keep
// "/"), and drop any query string that slipped in.
function _normPath(pathname) {
  if (!pathname) return '';
  const p = pathname.split('?')[0];
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
}

// Flat index of feature route → { label, typeId, verbId }, built once. Covers
// every routable feature (the same catalog getFeatureRoutes walks), so the
// title/breadcrumb lookups stay in lock-step with the sidebar.
let _routeIndex = null;
function _buildRouteIndex() {
  if (_routeIndex) return _routeIndex;
  const index = new Map();
  Object.values(FEATURE_NAVIGATION).forEach(category => {
    Object.values(category.types).forEach(type => {
      if (!type.to) return;
      index.set(_normPath(type.to), {
        label: type.label,
        typeId: type.id,
        verbId: TYPE_VERB[type.id] || null,
      });
    });
  });
  _routeIndex = index;
  return index;
}

/**
 * Resolve navigation context for a pathname — the single lookup that powers both
 * the page <title> and the ViewHeader breadcrumb, so they can never disagree.
 *
 * Returns { title, crumbs } where crumbs is an ordered list of
 * { label, to } from Home down to (but not including) the current page, plus the
 * current page as the last, non-linked crumb. Home itself yields no crumbs.
 *
 * @param {string} pathname - e.g. '/app/monitoring/dossier'
 * @returns {{ title: string|null, crumbs: Array<{label:string,to:string|null}> }}
 */
export function resolveNavForPath(pathname) {
  const p = _normPath(pathname);
  const home = { label: 'Home', to: VERB_LANDING.watch };

  // Home / Surveil landing — the root, no breadcrumb trail.
  if (p === VERB_LANDING.watch) {
    return { title: 'Home', crumbs: [] };
  }

  // A verb landing page itself → Home › Verb.
  const verbAtPath = Object.keys(VERB_LANDING).find(v => VERB_LANDING[v] === p);
  if (verbAtPath) {
    const verb = NAV_VERBS[verbAtPath];
    return { title: verb.label, crumbs: [home, { label: verb.label, to: null }] };
  }

  // The Arcade hub and any individual game route → Home › Inflict › Arcade[ › Game].
  if (p === '/app/games' || p.startsWith('/app/games/')) {
    const doVerb = NAV_VERBS.do;
    const crumbs = [home, { label: doVerb.label, to: VERB_LANDING.do }, { label: 'Arcade', to: '/app/games' }];
    if (p === '/app/games') {
      crumbs[crumbs.length - 1] = { label: 'Arcade', to: null };
      return { title: 'Arcade', crumbs };
    }
    const game = _buildRouteIndex().get(p);
    const gameLabel = game ? game.label : 'Game';
    crumbs.push({ label: gameLabel, to: null });
    return { title: gameLabel, crumbs };
  }

  // A catalogued feature route → Home › Verb › Feature.
  const feature = _buildRouteIndex().get(p);
  if (feature) {
    const crumbs = [home];
    if (feature.verbId && NAV_VERBS[feature.verbId]) {
      crumbs.push({ label: NAV_VERBS[feature.verbId].label, to: VERB_LANDING[feature.verbId] });
    }
    crumbs.push({ label: feature.label, to: null });
    return { title: feature.label, crumbs };
  }

  // Unknown route — no title override, no breadcrumb.
  return { title: null, crumbs: [] };
}
